import { useEffect, useRef, useState } from "react"
import DotsLoader from "components/DotsLoader"
import { getContractChainId, getContractConfigByAddress } from "utils/contractInfoHelper"
import { getArtBlocksImageUrl } from "utils/artBlocksUrls"

interface Props {
  contractAddress: string
  tokenId: string
  width: number
  height: number
  chainId?: number
  imageUrl?: string
  alt?: string
}

const TokenImage = ({ contractAddress, tokenId, width, height, chainId, imageUrl, alt }: Props) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const contractConfig = getContractConfigByAddress(contractAddress)
  const resolvedChainId = getContractChainId(contractAddress, chainId)
  const resolvedImageUrl = imageUrl || (resolvedChainId
    ? getArtBlocksImageUrl({ chainId: resolvedChainId, contractAddress, tokenId })
    : `${contractConfig?.MEDIA_URL}/${tokenId}.png`)

  useEffect(() => {
    setLoaded(false)
    setFailed(false)

    const image = imageRef.current
    if (!image?.complete) return

    if (image.naturalWidth > 0) {
      setLoaded(true)
    } else {
      setFailed(true)
    }
  }, [resolvedImageUrl])

  return (
    <div
      style={{ width, height }}
      className="bg-gallery-surface overflow-hidden relative flex items-center justify-center"
      aria-busy={!loaded && !failed}
    >
      {!loaded && !failed && (
        <div className="gallery-image-loader">
          <DotsLoader label="Loading token preview" shuffleKey={resolvedImageUrl} />
        </div>
      )}
      {failed ? (
        <span className="gallery-label text-gallery-muted px-4 text-center">Preview not loading</span>
      ) : (
        <img
          ref={imageRef}
          src={resolvedImageUrl}
          alt={alt || `Token ${tokenId}`}
          width={width}
          height={height}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-contain transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
          style={{ display: "block" }}
        />
      )}
    </div>
  )
}

export default TokenImage
